import { Asset, CustodyEvent } from '@/types/models';
import { AssetStatusBadge, ConditionBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Printer,
  ArrowRightLeft,
  RefreshCw,
  Camera,
  ExternalLink,
  MapPin,
  Tag,
  Hash,
  Building2,
  Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AssetQuickViewDrawerProps {
  asset: Asset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  custodyEvents?: CustodyEvent[];
}

export function AssetQuickViewDrawer({
  asset,
  open,
  onOpenChange,
  custodyEvents = [],
}: AssetQuickViewDrawerProps) {
  const navigate = useNavigate();

  if (!asset) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2 text-lg font-mono">
            <Tag className="h-5 w-5 text-primary" />
            {asset.barcode || asset.internal_barcode}
          </SheetTitle>
        </SheetHeader>

        {/* Status & Condition */}
        <div className="flex items-center gap-2 mb-6">
          <AssetStatusBadge status={asset.status} />
          <ConditionBadge condition={asset.condition} />
        </div>

        {/* Details Grid */}
        <div className="space-y-3 mb-6">
          <DetailRow icon={<Tag className="h-4 w-4" />} label="Category" value={asset.category_name} />
          <DetailRow icon={<Building2 className="h-4 w-4" />} label="Manufacturer" value={asset.manufacturer_name} />
          <DetailRow icon={<Hash className="h-4 w-4" />} label="Model" value={asset.model} />
          <DetailRow icon={<Hash className="h-4 w-4" />} label="Serial" value={asset.serial_number} />
          <DetailRow icon={<MapPin className="h-4 w-4" />} label="Site" value={asset.site_id} />
          {asset.imei && <DetailRow icon={<Hash className="h-4 w-4" />} label="IMEI" value={asset.imei} />}
          {asset.notes && <DetailRow icon={<Tag className="h-4 w-4" />} label="Notes" value={asset.notes} />}
        </div>

        <Separator className="my-4" />

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          <Button variant="outline" size="sm" className="gap-2">
            <Printer className="h-4 w-4" /> Print Label
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <ArrowRightLeft className="h-4 w-4" /> Move
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" /> Change Status
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <Camera className="h-4 w-4" /> Add Photo
          </Button>
        </div>

        <Separator className="my-4" />

        {/* Recent Movement History */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-foreground mb-3">Recent Activity</h4>
          {custodyEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No movement history yet.</p>
          ) : (
            <div className="space-y-3">
              {custodyEvents.slice(0, 5).map((event) => (
                <div key={event.id} className="flex gap-3 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="text-foreground">{event.action}</p>
                    {event.from_location && event.to_location && (
                      <p className="text-muted-foreground">
                        {event.from_location} → {event.to_location}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {new Date(event.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Full Detail Link */}
        <Button
          variant="default"
          className="w-full gap-2"
          onClick={() => {
            onOpenChange(false);
            navigate(`/assets/${asset.id}`);
          }}
        >
          <ExternalLink className="h-4 w-4" /> View Full Detail
        </Button>
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="text-muted-foreground mt-0.5 shrink-0">{icon}</span>
      <div className="flex gap-2">
        <span className="text-muted-foreground min-w-[80px]">{label}</span>
        <span className="text-foreground font-medium">{value}</span>
      </div>
    </div>
  );
}
